import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

export type ListingPlatform = 'airbnb' | 'vrbo' | 'booking_com';

export interface PropertyListing {
  id: number;
  platform: ListingPlatform;
  platformLabel: string;
  platformId: string;
  status: 'active' | 'inactive';
  connectedAt: string;
}

export interface ListingPhoto {
  id: string;
  caption: string;
  order: number;
  url: string;
}

/** One bed type in a bedroom — canonical slug from LISTING_BED_TYPES plus how many. */
export interface ListingRoomBed {
  type: string;
  count: number;
}

export interface ListingPhotoRoom {
  key: string;
  name: string;
  /** Platform space type the PMS resolves the room to (key-format parsing is its fallback). */
  type?: string;
  order: number;
  photos: ListingPhoto[];
  /** Bed types in this room. Only meaningful on bedroom-type rooms. */
  beds?: ListingRoomBed[];
}

export interface ListingEditFields {
  title: string;
  description: string;
  price: number | null;           // cents
  securityDeposit: number | null; // cents
  bedrooms: number | null;
  bathrooms: number | null;
  squareFeet: number | null;
  propertyType: string;
  amenities: Record<string, boolean>;
  photos: { rooms: ListingPhotoRoom[] };
}

export interface AmenityOption {
  slug: string;
  label: string;
  icon: string;
  /**
   * Set when the row is NOT part of the `amenities` blob but a setting of its own. Pets is
   * the only one today: the backend has no pets amenity — `pets_allowed` is its own
   * SYNC_MATRIX entry (policy_flag / guest_policies), which Booking Settings also edits.
   * Rows carrying this read and write that row instead, so both screens drive one value.
   */
  settingKey?: string;
  /** Category of `settingKey`'s row. Only meaningful alongside it. */
  settingCategory?: string;
}

// Canonical slugs mirroring UNIFIED_AMENITIES in the far backend (unified-fields.js)
export const UNIFIED_AMENITIES: AmenityOption[] = [
  { slug: 'wifi', label: 'WiFi', icon: 'wifi' },
  { slug: 'kitchen', label: 'Kitchen', icon: 'countertops' },
  { slug: 'pool', label: 'Pool', icon: 'pool' },
  { slug: 'hotTub', label: 'Hot Tub', icon: 'hot_tub' },
  // airConditioning, not centralAc: Airbnb collapses central/wall/window into one native AC
  // token, so a read can only resolve to the generic slug and that is what the backend stores.
  { slug: 'airConditioning', label: 'Air Conditioning', icon: 'ac_unit' },
  { slug: 'heating', label: 'Heating', icon: 'thermostat' },
  { slug: 'washer', label: 'Washer', icon: 'local_laundry_service' },
  { slug: 'dryer', label: 'Dryer', icon: 'dry_cleaning' },
  { slug: 'dishwasher', label: 'Dishwasher', icon: 'kitchen' },
  { slug: 'tv', label: 'TV', icon: 'tv' },
  { slug: 'furnished', label: 'Furnished', icon: 'chair' },
  { slug: 'elevator', label: 'Elevator', icon: 'elevator' },
  { slug: 'freeParking', label: 'Free Parking', icon: 'local_parking' },
  { slug: 'gym', label: 'Gym', icon: 'fitness_center' },
  { slug: 'fireplace', label: 'Fireplace', icon: 'fireplace' },
  { slug: 'smokeAlarm', label: 'Smoke Alarm', icon: 'sensors' },
  { slug: 'balcony', label: 'Balcony', icon: 'balcony' },
  { slug: 'bbq', label: 'BBQ Grill', icon: 'outdoor_grill' },
  { slug: 'petsAllowed', label: 'Pets Allowed', icon: 'pets', settingKey: 'pets_allowed', settingCategory: 'optimizer' },
  { slug: 'dedicatedWorkspace', label: 'Dedicated Workspace', icon: 'computer' },
];

export const LISTING_PROPERTY_TYPES = [
  { value: 'house', viewValue: 'House' },
  { value: 'apartment', viewValue: 'Apartment' },
  { value: 'condo', viewValue: 'Condo' },
  { value: 'townhouse', viewValue: 'Townhouse' },
  { value: 'guesthouse', viewValue: 'Guesthouse' },
  { value: 'cabin', viewValue: 'Cabin' },
];

const PLATFORM_LABELS: Record<ListingPlatform, string> = {
  airbnb: 'Airbnb',
  vrbo: 'VRBO',
  booking_com: 'Booking.com',
};

const SAVE_DELAY_MS = 1200;

/**
 * Illustrative mock of the upcoming listings backend (POST /api/v1/listings/edit).
 * Every address gets the same three connected listings and a seeded set of unified
 * edit fields. Edits persist in memory only so saving can be demoed end to end
 * without hitting any endpoint.
 */
@Injectable({ providedIn: 'root' })
export class PropertyListingsMockService {

  private listingsByAddress = new Map<number, PropertyListing[]>();
  private editFieldsByAddress = new Map<number, ListingEditFields>();

  getListings(addressId: number): PropertyListing[] {
    if (!this.listingsByAddress.has(addressId)) {
      this.listingsByAddress.set(addressId, this.buildListings(addressId));
    }
    return this.listingsByAddress.get(addressId);
  }

  getEditFields(addressId: number): ListingEditFields {
    if (!this.editFieldsByAddress.has(addressId)) {
      this.editFieldsByAddress.set(addressId, this.buildEditFields(addressId));
    }
    return this.editFieldsByAddress.get(addressId);
  }

  /**
   * Mirrors the target request shape: { edits: {field: value}, targets: [...] }.
   * Merges the edits locally and resolves after a delay so the UI can show
   * its saving indicator.
   */
  saveEdits(addressId: number, edits: Partial<ListingEditFields>): Observable<ListingEditFields> {
    const current = this.getEditFields(addressId);
    const merged: ListingEditFields = {
      ...current,
      ...edits,
      amenities: { ...current.amenities, ...(edits.amenities || {}) },
      photos: edits.photos ? this.clonePhotos(edits.photos) : current.photos,
    };
    this.editFieldsByAddress.set(addressId, merged);
    return of(merged).pipe(delay(SAVE_DELAY_MS));
  }

  setListingStatus(addressId: number, listingId: number, status: 'active' | 'inactive'): Observable<PropertyListing[]> {
    const updated = this.getListings(addressId).map(listing =>
      listing.id === listingId ? { ...listing, status } : listing
    );
    this.listingsByAddress.set(addressId, updated);
    return of(updated).pipe(delay(SAVE_DELAY_MS));
  }

  buildPlaceholderPhoto(addressId: number, roomKey: string, index: number): ListingPhoto {
    return {
      id: `photo-${addressId}-${roomKey}-${index}`,
      caption: '',
      order: index,
      url: `https://picsum.photos/seed/tidy-${addressId}-${roomKey}-${index}/640/420`,
    };
  }

  private buildListings(addressId: number): PropertyListing[] {
    const platforms: ListingPlatform[] = ['airbnb', 'vrbo', 'booking_com'];
    return platforms.map((platform, index) => ({
      id: addressId * 10 + index,
      platform,
      platformLabel: PLATFORM_LABELS[platform],
      platformId: String(10000000 + (addressId * 37 + index * 13) % 89999999),
      status: 'active',
      connectedAt: '2026-05-12T00:00:00Z',
    }));
  }

  private buildEditFields(addressId: number): ListingEditFields {
    const seed = addressId % 5;
    const titles = [
      'Sunny Retreat Near Downtown',
      'Modern Getaway with Fast WiFi',
      'Cozy Home Close to Everything',
      'Stylish Stay with Private Patio',
      'Bright & Spacious Family Home',
    ];
    const amenities: Record<string, boolean> = {};
    UNIFIED_AMENITIES.forEach((amenity, index) => {
      amenities[amenity.slug] = (index + seed) % 3 !== 0;
    });
    const rooms: ListingPhotoRoom[] = [
      { key: 'living_room', name: 'Living Room', order: 0, photos: [] },
      { key: 'kitchen', name: 'Kitchen', order: 1, photos: [] },
      { key: 'bedroom_1', name: 'Bedroom 1', order: 2, photos: [] },
      { key: 'bathroom_1', name: 'Bathroom 1', order: 3, photos: [] },
    ];
    const captions = [
      'Plenty of natural light',
      'Freshly cleaned before every stay',
      'Comfortable and quiet',
    ];
    rooms.forEach((room, roomIndex) => {
      const photoCount = 2 + ((seed + roomIndex) % 2);
      for (let i = 0; i < photoCount; i++) {
        const photo = this.buildPlaceholderPhoto(addressId, room.key, i);
        photo.caption = captions[(roomIndex + i) % captions.length];
        room.photos.push(photo);
      }
    });
    return {
      title: titles[seed],
      description: 'Enjoy a relaxing stay at this well-kept home. Walking distance to shops and restaurants, with a fully stocked kitchen, fast WiFi, and hotel-quality linens. Professionally cleaned and restocked between every stay.',
      price: (12500 + seed * 1500),
      securityDeposit: (25000 + seed * 5000),
      bedrooms: 2 + (seed % 3),
      bathrooms: seed % 2 === 0 ? 2 : 2.5,
      squareFeet: 1200 + seed * 180,
      propertyType: LISTING_PROPERTY_TYPES[seed % LISTING_PROPERTY_TYPES.length].value,
      amenities,
      photos: { rooms },
    };
  }

  private clonePhotos(photos: { rooms: ListingPhotoRoom[] }): { rooms: ListingPhotoRoom[] } {
    return {
      rooms: photos.rooms.map(room => ({
        ...room,
        photos: room.photos.map(photo => ({ ...photo })),
      })),
    };
  }
}
